
import { defineComponent } from "vue";
import { IPhieuNhapKhoResponseBase } from "@/core/interfaces/ApiResponses";
import { PhieuNhapKhoService } from "@/core/services/PhieuNhapKho.service";
import ToolService from "@/core/services/Tool.service";
import {
  calcLineAmount,
  formatAmount,
  formatQuantity,
  formatUnitPrice,
} from "@/core/helpers/number-format";
import {
  swalErrNotification,
  transformFirstLetterToUpperCase,
} from "@/core/helpers/utils";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import PdfViewerPage from "@/components/shared/pdf-viewer/PdfViewerPage.vue";
import PdfDocumentHeader from "@/components/shared/pdf-viewer/PdfDocumentHeader.vue";
import PdfAccountingHeader from "@/components/shared/pdf-viewer/PdfAccountingHeader.vue";
import { generateHtmlElementPdf } from "@/components/shared/pdf-viewer/generate-html-element-pdf";
import { formatTaiKhoanMaCt, pickDonViPdfExtra } from "@/components/shared/pdf-viewer/pdf-document-header";
import {
  getPdfContentWidthPx,
  getPdfPrintContentStyle,
} from "@/components/shared/pdf-viewer/pdf-content.config";

export default defineComponent({
  name: "PhieuNhapKhoViewer",

  components: { PdfViewerPage, PdfDocumentHeader, PdfAccountingHeader },

  setup() {
    const { moment } = useDateTime();
    return { moment };
  },

  data() {
    return {
      phieuNhapData: null as IPhieuNhapKhoResponseBase | null,
      pdfSource: null as string | null,
      isGenerating: false,
      tongTienInText: "",
      tenDonVi: "",
      tenDonViCha: "",
    };
  },

  computed: {
    taiKhoanNo(): string {
      const tkNo = this.phieuNhapData?.tk_no;
      return formatTaiKhoanMaCt(tkNo?.ma_tk, tkNo?.ma_ct);
    },

    taiKhoanCo(): string {
      return formatTaiKhoanMaCt(
        this.phieuNhapData?.ma_tk_co,
        this.phieuNhapData?.ma_ct_co
      );
    },

    pdfFileName(): string {
      if (!this.phieuNhapData) return "phieu-nhap-kho.pdf";
      return `phieu-nhap-kho-${this.phieuNhapData.so_phieu}.pdf`;
    },

    tenTkCoDisplay(): string {
      const ten = this.phieuNhapData?.ten_tk_co;
      if (!ten || ten.toLowerCase() === "không xác định") {
        return ".............................";
      }
      return ten;
    },

    ngayNhapKhoDisplay(): string {
      if (!this.phieuNhapData?.created_at) return "";
      const date = new Date(this.phieuNhapData.created_at);
      return `Ngày ${date.getDate()} tháng ${
        date.getMonth() + 1
      } năm ${date.getFullYear()}`;
    },

    soHopDongDisplay(): string {
      if (!this.phieuNhapData) return "";
      if (+this.phieuNhapData.hop_dong) {
        return this.phieuNhapData.hop_dong.so_hop_dong;
      }
      return this.phieuNhapData.so_hop_dong ?? "";
    },

    ngayHopDongDisplay(): string {
      const ngay =
        this.phieuNhapData?.ngay_hop_dong ||
        this.phieuNhapData?.hop_dong?.ngay_ky;
      if (!ngay) return "";
      return this.moment(ngay).format("DD/MM/YYYY");
    },

    ngayHoaDonDisplay(): string {
      if (!this.phieuNhapData?.ngay_hoa_don) return "";
      return this.moment(this.phieuNhapData.ngay_hoa_don).format("DD/MM/YYYY");
    },

    totalPrice(): number {
      if (!this.phieuNhapData) return 0;
      return this.phieuNhapData.vat_tu_nhap_kho.reduce(
        (sum, { pivot: { don_gia, so_luong } }) => sum + +so_luong * +don_gia,
        0
      );
    },

    signatureItems(): { label: string; name?: string }[] {
      return [
        { label: "Người giao hàng", name: this.phieuNhapData?.nguoi_giao },
        { label: "Thủ kho" },
        { label: "Phòng KH-VT" },
        { label: "P.Tài chính" },
        {
          label: "Giám đốc",
          name: this.phieuNhapData?.bien_ban_kiem_nghiem?.ten_giam_doc,
        },
      ];
    },

    printPaperStyle(): Record<string, string> {
      return getPdfPrintContentStyle();
    },

    hiddenTemplateStyle(): Record<string, string> {
      return { width: `${getPdfContentWidthPx()}px` };
    },
  },

  methods: {
    calcLineAmount,
    formatAmount,
    formatQuantity,
    formatUnitPrice,

    async updateTongTienInText() {
      if (!this.phieuNhapData) {
        this.tongTienInText = "";
        return;
      }

      const {
        data: { data },
      } = await ToolService.convertPriceToText(this.totalPrice);

      this.tongTienInText = transformFirstLetterToUpperCase(data);
    },

    async loadAndGenerate() {
      const id = Number(this.$route.params.id);
      if (!id) return;

      try {
        this.isGenerating = true;
        const { data: response } = await PhieuNhapKhoService.get(id);
        this.phieuNhapData = response.data;
        const { tenDonVi, tenDonViCha } = pickDonViPdfExtra(response.extra);
        this.tenDonVi = tenDonVi;
        this.tenDonViCha = tenDonViCha;

        await this.updateTongTienInText();

        await this.$nextTick();
        await this.$nextTick();

        await this.generatePdf();
      } catch (e) {
        swalErrNotification(e, "Có lỗi khi tải phiếu nhập kho");
      } finally {
        this.isGenerating = false;
      }
    },

    async generatePdf() {
      const el = (this.$refs.printArea as HTMLElement)?.querySelector(
        ".printing-paper"
      ) as HTMLElement | null;
      if (!el) return;

      this.pdfSource = await generateHtmlElementPdf(el);
    },
  },

  mounted() {
    this.loadAndGenerate();
  },
});
